import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { LanguageProvider } from "@/i18n/LanguageContext";
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const AdminAuthLayout = lazy(() => import("@/components/AdminAuthLayout"));

// Mount the cookie banner only once the page has loaded and the browser is idle,
// so it stays out of the critical request chain.
const DeferredCookieConsent = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => {
      const ric = (window as any).requestIdleCallback as
        | ((cb: () => void, o?: { timeout: number }) => number)
        | undefined;
      if (ric) ric(() => setReady(true), { timeout: 2500 });
      else window.setTimeout(() => setReady(true), 1500);
    };
    if (document.readyState === "complete") go();
    else window.addEventListener("load", go, { once: true });
    return () => window.removeEventListener("load", go);
  }, []);
  return ready ? (
    <Suspense fallback={null}>
      <CookieConsent />
    </Suspense>
  ) : null;
};

import Index from "./pages/Index";

const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminSignupPage = lazy(() => import("./pages/AdminSignupPage"));
const AdminForgotPasswordPage = lazy(() => import("./pages/AdminForgotPasswordPage"));
const AdminResetPasswordPage = lazy(() => import("./pages/AdminResetPasswordPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const BaerekraftPage = lazy(() => import("./pages/BaerekraftPage"));
const LaboratoriumPage = lazy(() => import("./pages/LaboratoriumPage"));
const JorddampeanleggPage = lazy(() => import("./pages/JorddampeanleggPage"));
const JordvaskeanleggPage = lazy(() => import("./pages/JordvaskeanleggPage"));
const MottakPage = lazy(() => import("./pages/MottakPage"));
const KunnskapssenterPage = lazy(() => import("./pages/KunnskapssenterPage"));
const ISPSKaiPage = lazy(() => import("./pages/ISPSKaiPage"));
const SalgsbetingelserPage = lazy(() => import("./pages/SalgsbetingelserPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const GjenvunneMasserPage = lazy(() => import("./pages/GjenvunneMasserPage"));
const PriskalkulatorPage = lazy(() => import("./pages/PriskalkulatorPage"));
const OmOssPage = lazy(() => import("./pages/OmOssPage"));
const NyheterPage = lazy(() => import("./pages/NyheterPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const KontaktPage = lazy(() => import("./pages/KontaktPage"));
const VaartStyrePage = lazy(() => import("./pages/VaartStyrePage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const PersonvernPage = lazy(() => import("./pages/PersonvernPage"));
const VilkarPage = lazy(() => import("./pages/VilkarPage"));

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};



const FullSite = () => (
  <>
    <ScrollToTop />
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/baerekraft" element={<BaerekraftPage />} />
        <Route path="/laboratorium" element={<LaboratoriumPage />} />
        <Route path="/jorddampeanlegg" element={<JorddampeanleggPage />} />
        <Route path="/jordvaskeanlegg" element={<JordvaskeanleggPage />} />
        <Route path="/mottak" element={<MottakPage />} />
        <Route path="/kompetansesenter" element={<KunnskapssenterPage />} />
        <Route path="/kunnskapssenter" element={<Navigate to="/kompetansesenter" replace />} />
        <Route path="/isps-kai" element={<ISPSKaiPage />} />
        <Route path="/salgsbetingelser" element={<SalgsbetingelserPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/gjenvunne-masser" element={<GjenvunneMasserPage />} />
        <Route path="/priskalkulator" element={<PriskalkulatorPage />} />
        <Route path="/om-oss" element={<OmOssPage />} />
        <Route path="/nyheter" element={<NyheterPage />} />
        <Route path="/blogg/:slug" element={<BlogPostPage />} />
        <Route path="/kontakt" element={<KontaktPage />} />
        <Route path="/vaart-styre" element={<VaartStyrePage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/ofte-stilte-sporsmal" element={<FAQPage />} />
        <Route path="/personvern" element={<PersonvernPage />} />
        <Route path="/vilkar" element={<VilkarPage />} />
        <Route element={<AdminAuthLayout />}>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/signup" element={<AdminSignupPage />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    <DeferredCookieConsent />
  </>
);




export const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <FullSite />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
